import { Menu } from 'antd';
import clsx from 'clsx';
import AnimatedSkeletonLoader from 'components/AnimatedSkeletonLoader';
import {
  darkModeSelector,
  selectedMenuKeysSelector,
  selectedOpenKeysSelector,
} from 'features/appUI/selectors';
import { setSelectedMenuKeys, setSelectedOpenKeys } from 'features/appUI/slice';
import {
  currentUserIsAdminSelector,
  currentUserIsSuperAdminSelector,
  currentUserSelector,
} from 'features/auth/selectors';
import {
  useGetModuleListQuery,
  useGetModulesByUserNameQuery,
} from 'features/module/query';
import { TModule } from 'features/module/type';
import styles from 'layouts/MainLayout/AppMainSidebar/AppMainSidebar.module.scss';
import { menuItems } from 'layouts/MainLayout/AppMainSidebar/constants';
import { filterMenuItemsByPermissions } from 'layouts/MainLayout/helper';
import { useAppDispatch, useAppSelector } from 'lib/stores/hook';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const AppMainSidebar = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const selectedMenuKeys = useAppSelector(selectedMenuKeysSelector);
  const selectedOpenKeys = useAppSelector(selectedOpenKeysSelector);
  const isDarkMode = useAppSelector(darkModeSelector);
  const currentUser = useAppSelector(currentUserSelector);

  const { username } = currentUser || {};

  const isSuperAdmin = useAppSelector(currentUserIsSuperAdminSelector);
  const isPOAdmin = useAppSelector(currentUserIsAdminSelector);

  const { data: adminVPDTModules, isFetching: isFetchingAdminModules } =
    useGetModuleListQuery(undefined, {
      skip: !isPOAdmin,
    });

  const { data: userModules, isFetching: isFetchingUserModules } =
    useGetModulesByUserNameQuery(username as string, {
      skip: !username || isPOAdmin || isSuperAdmin,
    });

  const appModules = (adminVPDTModules || userModules) as TModule[];

  const items = useMemo(() => {
    if (isSuperAdmin) return menuItems;
    if (isPOAdmin && !adminVPDTModules) return [];
    if (!isPOAdmin && !userModules) return [];

    return filterMenuItemsByPermissions(menuItems, appModules);
  }, [adminVPDTModules, appModules, isPOAdmin, isSuperAdmin, userModules]);

  const handleClickMenuItem = useCallback(
    ({ key }: { key: string }) => navigate(key),
    [navigate]
  );

  const checkMatchPath = useCallback(() => {
    menuItems.forEach(menuItem => {
      const isChildrenItemSelected = Boolean(
        menuItem?.children?.some(child => child.key === pathname)
      );
      if (isChildrenItemSelected) {
        dispatch(setSelectedOpenKeys([menuItem.key]));
      }
    });
  }, [dispatch, pathname]);

  useEffect(() => {
    dispatch(setSelectedMenuKeys([pathname]));
    checkMatchPath();
  }, [checkMatchPath, dispatch, pathname]);

  if (!username) return null;

  if (isFetchingAdminModules || isFetchingUserModules)
    return <AnimatedSkeletonLoader rows={3} />;

  if (isPOAdmin && !adminVPDTModules) return null;
  if (!isPOAdmin && !isSuperAdmin && !userModules) return null;

  return (
    <Menu
      mode="inline"
      items={items}
      theme={isDarkMode ? 'dark' : 'light'}
      onClick={handleClickMenuItem}
      selectedKeys={selectedMenuKeys}
      openKeys={selectedOpenKeys}
      onOpenChange={openKeys => dispatch(setSelectedOpenKeys(openKeys))}
      className={clsx(styles.appMenu, 'w-full !border-0')}
    />
  );
};

export default memo(AppMainSidebar);
